import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import OriginTracing from '@atlassiansox/origin-tracing';

import { SpotlightTransition } from '@atlaskit/onboarding';
import { token, useThemeObserver } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useChoreographerAssets } from '@confluence/choreographer-services';
import {
	ExperienceSuccess,
	STANDARD_DIFFERENTIATORS_HELP_EXPERIENCE,
	STANDARD_DIFFERENTIATORS_ONBOARDING_EXPERIENCE,
} from '@confluence/experience-tracker';

import { usePaymentStatus } from '../../hooks/usePaymentStatus';
import PaymentDetailsSpotlight from '../PaymentDetailsSpotlight.svg';
import PaymentDetailsSpotlightDark from '../PaymentDetailsSpotlightDark.svg';
import { VideoTutorialModal } from '../video-tutorial-modal';

const i18n = defineMessages({
	spotlight1Message: {
		id: 'experiment-standard-differentiators.spotlight.1.message',
		defaultMessage: `Rewatch the video and check our in-app docs and support resources here.`,
		description:
			'Message for help menu spotlight that informs users they can rewatch the Standard trial onboarding video',
	},
	spotlight1Action: {
		id: 'experiment-standard-differentiators.spotlight.1.action',
		defaultMessage: 'Got it',
		description:
			'Button to dismiss help menu spotlight that informs users they can rewatch the Standard trial onboarding video',
	},
	spotlight2Heading: {
		id: 'experiment-standard-differentiators.spotlight.2.heading',
		defaultMessage: 'Add payment details for the Standard plan',
		description: 'Heading for Edition Awareness spotlight',
	},
	spotlight2Message: {
		id: 'experiment-standard-differentiators.spotlight.2.message',
		defaultMessage:
			'Before your trial ends, add your payment details to keep your team going with the full value of the Confluence Standard plan.',
		description: 'Message for for Edition Awareness spotlight',
	},
	spotlight2Action: {
		id: 'experiment-standard-differentiators.spotlight.2.action',
		defaultMessage: 'Add now',
		description: 'Button that takes users to the payment details form',
	},
	spotlight2SecondaryAction: {
		id: 'experiment-standard-differentiators.spotlight.2.secondary-action',
		defaultMessage: 'Maybe later',
		description:
			'Button to dismiss edition awareness spotlight that asks users to input payment details',
	},
	helpButtonVideoTutorial: {
		id: 'experiment-standard-differentiators.in-product-help.resource.video.tutorial',
		defaultMessage: 'Video tutorials',
		description: 'A button to display a video that shows the features of Confluence Standard',
	},
});

export const STANDARD_DIFF_ANALYTICS_SOURCE = 'standard-differentiators';

export type Props = {
	touchpointId: string;
	onClose: () => void;
	hasBillingInfo: boolean | null;
};

export const DifferentiatorsModal = ({ touchpointId, onClose, hasBillingInfo }: Props) => {
	const { Spotlight } = useChoreographerAssets();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const { colorMode } = useThemeObserver();

	const { paymentUrl } = usePaymentStatus();

	const [isVideoTutorialOpen, setIsVideoTutorialOpen] = useState<boolean>(true);
	const [activeSpotlight, setActiveSpotlight] = useState<number | null>(0);

	const next = () => setActiveSpotlight((activeSpotlight || 0) + 1);

	const end = () => {
		setActiveSpotlight(null);
		onClose();
	};

	const targets = ['app-navigation-help', 'edition-awareness'];

	const getSpotlightTarget = (index: number) => {
		return targets[index - 1];
	};

	const handleClose = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'closeVideoTutorialButton',
				source: STANDARD_DIFF_ANALYTICS_SOURCE,
				attributes: {
					billingInfo: hasBillingInfo,
					touchpointId,
				},
			},
		}).fire();

		setIsVideoTutorialOpen(false);

		if (hasBillingInfo === false) {
			next();
		} else {
			end();
		}
	};

	const redirect = (paymentUrl: string) => {
		const origin = new OriginTracing({ product: 'confluence' });
		const redirectUrlWithReferrer = origin.addToUrl(paymentUrl);

		const originAttribute = origin.toAnalyticsAttributes({
			hasGeneratedId: true,
		});

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'addNowButton',
				source: STANDARD_DIFF_ANALYTICS_SOURCE,
				attributes: {
					originAttribute,
					billingInfo: hasBillingInfo,
					touchpointId,
				},
			},
		}).fire();

		window.open(redirectUrlWithReferrer, '_blank', 'noreferrer');
	};

	const renderActiveSpotlight = () => {
		const getSpotlight = (index: number) => {
			if (index === 0) {
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						name: 'videoTutorialModal',
						attributes: {
							billingInfo: hasBillingInfo,
							source: STANDARD_DIFF_ANALYTICS_SOURCE,
							touchpointId,
						},
					},
				}).fire();

				return (
					<div>
						{isVideoTutorialOpen && (
							<VideoTutorialModal
								onClose={handleClose}
								hasBillingInfo={hasBillingInfo}
								touchpointId={touchpointId}
							/>
						)}
					</div>
				);
			}
			if (index === 1) {
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						name: 'rewatchSpotlight',
						attributes: {
							billingInfo: hasBillingInfo,
							source: STANDARD_DIFF_ANALYTICS_SOURCE,
							touchpointId,
						},
					},
				}).fire();

				return (
					<Spotlight
						actions={[
							{
								text: formatMessage(i18n.spotlight1Action),
								onClick: () => {
									createAnalyticsEvent({
										type: 'sendUIEvent',
										data: {
											action: 'clicked',
											actionSubject: 'rewatchGotItButton',
											source: STANDARD_DIFF_ANALYTICS_SOURCE,
											attributes: {
												billingInfo: hasBillingInfo,
												touchpointId,
											},
										},
									}).fire();
									next();
								},
							},
						]}
						target={getSpotlightTarget(index)}
						key={getSpotlightTarget(index)}
						targetRadius={3}
						targetBgColor={token('color.text.inverse')}
						dialogPlacement="bottom right"
						dialogWidth={275}
					>
						{formatMessage(i18n.spotlight1Message)}
					</Spotlight>
				);
			}
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'addPaymentDetailsSpotlight',
					attributes: {
						billingInfo: hasBillingInfo,
						source: STANDARD_DIFF_ANALYTICS_SOURCE,
						touchpointId,
					},
				},
			}).fire();

			return (
				<>
					<Spotlight
						actions={[
							{
								text: formatMessage(i18n.spotlight2Action),
								onClick: () => {
									end();
									redirect(paymentUrl);
								},
							},
							{
								appearance: 'subtle',
								onClick: () => {
									createAnalyticsEvent({
										type: 'sendUIEvent',
										data: {
											action: 'clicked',
											actionSubject: 'maybeLaterButton',
											source: STANDARD_DIFF_ANALYTICS_SOURCE,
											attributes: {
												billingInfo: hasBillingInfo,
												touchpointId,
											},
										},
									}).fire();

									end();
								},
								text: formatMessage(i18n.spotlight2SecondaryAction),
							},
						]}
						heading={formatMessage(i18n.spotlight2Heading)}
						target={getSpotlightTarget(index)}
						key={getSpotlightTarget(index)}
						targetRadius={3}
						targetBgColor={token('color.text.inverse')}
						dialogPlacement="bottom right"
						image={colorMode === 'dark' ? PaymentDetailsSpotlightDark : PaymentDetailsSpotlight}
						dialogWidth={335}
						pulse={false}
					>
						{formatMessage(i18n.spotlight2Message)}
					</Spotlight>
					<ExperienceSuccess
						name={
							touchpointId == 'trial-onboarding'
								? STANDARD_DIFFERENTIATORS_ONBOARDING_EXPERIENCE
								: STANDARD_DIFFERENTIATORS_HELP_EXPERIENCE
						}
					/>
				</>
			);
		};

		if (activeSpotlight === null) {
			return null;
		}

		return getSpotlight(activeSpotlight);
	};

	return <SpotlightTransition>{renderActiveSpotlight()}</SpotlightTransition>;
};
